import axios, { HttpStatusCode } from 'axios';
import { TOURNAMENT_ERROR_MESSAGES } from '../../corelogic/domain/errors/gateway.errorMessages/tournament.error.messages';
import { TournamentError } from '../../corelogic/domain/errors/tournament.error';
import {
  CleanTournament,
  TournamentParticipant,
  TournamentUpsert,
  UserTournamentUpsert,
} from '../../corelogic/domain/models/tournament.model';
import { TournamentsGateway } from '../../corelogic/domain/ports/tournaments.port';
import {
  sanitizeInput,
  sanitizeTournamentUpsert,
} from '../../corelogic/domain/utils/inputSanitization.utils';
import { withRateLimit } from '../../corelogic/domain/utils/rateLimiting.utils';
import { getSecureRequestHeaders } from '../../corelogic/domain/utils/securityHeaders.utils';
import { handleAuthError } from './auth.error.handler';

export class ApiTournamentsGateway implements TournamentsGateway {
  private apiEndpoint: string;

  constructor(apiEndpoint: string) {
    this.apiEndpoint = apiEndpoint;
  }

  private handleTournamentError(error: unknown): never {
    if (!axios.isAxiosError(error)) {
      throw new TournamentError(TOURNAMENT_ERROR_MESSAGES.SERVER_ERROR);
    }
    if (error.response?.data?.error?.includes('token')) {
      throw handleAuthError(error);
    }
    switch (error.response?.status) {
      case HttpStatusCode.BadRequest:
        throw new TournamentError(TOURNAMENT_ERROR_MESSAGES.BAD_REQUEST);
      case HttpStatusCode.Unauthorized:
        throw new TournamentError(TOURNAMENT_ERROR_MESSAGES.UNAUTHORIZED);
      case HttpStatusCode.Forbidden:
        throw new TournamentError(TOURNAMENT_ERROR_MESSAGES.FORBIDDEN);
      default:
        throw new TournamentError(TOURNAMENT_ERROR_MESSAGES.SERVER_ERROR);
    }
  }

  // SIMPLE READ OPERATIONS: No rate limiting needed for basic data fetching
  async retrieveTournaments(token: string): Promise<CleanTournament[]> {
    try {
      const response = await axios.get<{ tournaments: CleanTournament[] }>(
        `${this.apiEndpoint}/tournaments`,
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.tournaments;
    } catch (error) {
      throw this.handleTournamentError(error);
    }
  }

  async retrieveTournamentsByUser(token: string): Promise<CleanTournament[]> {
    try {
      const response = await axios.get<{ tournaments: CleanTournament[] }>(
        `${this.apiEndpoint}/tournaments/user`,
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.tournaments;
    } catch (error) {
      throw this.handleTournamentError(error);
    }
  }

  async retrieveTournamentsCreatedByUser(
    token: string,
  ): Promise<CleanTournament[]> {
    try {
      const response = await axios.get<{ tournaments: CleanTournament[] }>(
        `${this.apiEndpoint}/tournaments/createdByUser`,
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
      return response.data.tournaments;
    } catch (error) {
      throw this.handleTournamentError(error);
    }
  }

  // COMPLEX DATA RETRIEVAL: Light rate limiting for performance protection
  retrieveTournamentDetails = withRateLimit(
    async (
      token: string,
      tournamentId: string,
      seasonIds: string[],
    ): Promise<TournamentParticipant[]> => {
      try {
        const response = await axios.post<{
          tournamentUserSeasonPoints: TournamentParticipant[];
        }>(
          `${this.apiEndpoint}/tournaments/${tournamentId}/userSeasonPoints`,
          { seasonIds },
          {
            headers: {
              ...getSecureRequestHeaders(token),
            },
          },
        );
        return response.data.tournamentUserSeasonPoints;
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'lowRisk', // 🟢 30/min - Complex read operation, but still low risk
    'tournament-details',
  );

  retrieveGlobalTournamentDetails = withRateLimit(
    async (
      token: string,
      limit: number,
      year?: number,
      seasonId?: string,
    ): Promise<TournamentParticipant[]> => {
      try {
        const response = await axios.post<{
          publicTournamentUserSeasonPoints: TournamentParticipant[];
        }>(
          `${this.apiEndpoint}/tournaments/publicTournamentUserSeasonPoints`,
          { year, limit, seasonId },
          {
            headers: {
              ...getSecureRequestHeaders(token),
            },
          },
        );
        return response.data.publicTournamentUserSeasonPoints;
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'lowRisk', // 🟢 30/min - Complex read operation, but still low risk
    'public-tournament-details',
  );

  // HIGH RISK OPERATIONS: Tournament creation and updates
  upsertTournament = withRateLimit(
    async (
      token: string,
      tournament: TournamentUpsert,
      tournamentId?: string,
    ): Promise<TournamentUpsert> => {
      try {
        console.log(
          'upsertTournament called with tournament: ',
          tournament,
          'and tournamentId: ',
          tournamentId,
        );

        // Sanitize input data before sending
        const sanitizedTournament = sanitizeTournamentUpsert(tournament);

        const response = await axios.post<TournamentUpsert>(
          tournamentId
            ? `${this.apiEndpoint}/tournaments/upsert/${tournamentId}`
            : `${this.apiEndpoint}/tournaments/upsert`,
          sanitizedTournament,
          {
            headers: {
              ...getSecureRequestHeaders(token),
            },
          },
        );
        console.log('gateway upsertTournament response: ', response.data);
        return response.data;
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'highRisk', // 🟠 5/min - Tournament creation/updates are high risk
    'tournament-upsert',
  );

  deleteTournament = withRateLimit(
    async (token: string, tournamentId: string): Promise<void> => {
      try {
        await axios.delete(`${this.apiEndpoint}/tournaments/${tournamentId}`, {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        });
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'criticalOperations', // 🔴 3/min - Tournament deletion is CRITICAL
    'tournament-delete',
    'Too many delete attempts. Please wait before trying to delete the tournament again.',
  );

  // USER TOURNAMENT OPERATIONS: Joining/leaving tournaments
  upsertUserTournament = withRateLimit(
    async (
      token: string,
      userTournament: UserTournamentUpsert,
    ): Promise<void> => {
      try {
        await axios.post<string>(
          `${this.apiEndpoint}/userTournament`,
          userTournament,
          {
            headers: {
              ...getSecureRequestHeaders(token),
            },
          },
        );
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'highRisk', // 🟠 5/min - Joining tournaments is high risk (form submission)
    'user-tournament-join',
  );

  deleteUserTournament = withRateLimit(
    async (token: string, tournamentId: string): Promise<void> => {
      try {
        await axios.delete(
          `${this.apiEndpoint}/userTournament/${tournamentId}`,
          {
            headers: {
              ...getSecureRequestHeaders(token),
            },
          },
        );
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'highRisk', // 🟠 5/min - Leaving tournaments is high risk but not critical
    'user-tournament-leave',
  );

  // PASSWORD VALIDATION: Medium risk due to potential brute force
  validateTournamentPassword = withRateLimit(
    async (tournamentId: string, password: string): Promise<boolean> => {
      try {
        // Sanitize password before sending
        const sanitizedPassword = sanitizeInput(
          password,
          'TOURNAMENT_PASSWORD',
        ).cleanValue;

        const response = await axios.post<{ isValid: boolean }>(
          `${this.apiEndpoint}/tournaments/validatePassword/${tournamentId}`,
          { password: sanitizedPassword },
          {
            headers: {
              'Content-Type': 'application/json',
              ...getSecureRequestHeaders(),
            },
          },
        );
        return response.data.isValid;
      } catch (error) {
        throw this.handleTournamentError(error);
      }
    },
    'mediumRisk', // 🟡 15/min - Password validation needs protection from brute force
    'tournament-password-validation',
  );
}
